"use client";

import { z } from "zod";

import type { GlyphLink, GlyphSessionApi } from "@/lib/wallet/glyph-account";

const LINK_URL = "/api/game/identity/glyph/";

const answer = z.union([
  z.object({
    wallet: z.string(),
    username: z.string().nullable(),
    pfp: z.string().nullable(),
  }),
  z.object({ code: z.literal("NOT_PROVEN") }),
  z.object({
    code: z.enum(["LINKED_ELSEWHERE", "PROOF_REQUIRED"]),
    linked: z.string().nullable(),
    wallet: z.string(),
  }),
]);

/**
 * Asks the server to link the account to the wallet this browser proved. The
 * server takes the wallet from its own Sign-In-With-Solana record; the
 * connected address is only compared, so a seat proven for another account
 * of the wallet reads as unproven here rather than as somewhere packs go.
 */
export async function postLink(
  session: GlyphSessionApi,
  connected: string,
  replace: boolean,
  current?: () => boolean
): Promise<GlyphLink> {
  const tokens = await session.tokens();
  if (!tokens) throw new Error("Sign in to Glyph again.");
  if (current && !current()) return { state: "not-proven" };
  const response = await fetch(LINK_URL, {
    method: "POST",
    headers: {
      authorization: `Bearer ${tokens.identityToken}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({
      accessToken: tokens.accessToken,
      expectedWallet: connected,
      ...(replace ? { replace: true } : {}),
    }),
    signal: AbortSignal.timeout(15_000),
  });
  if (current && !current()) return { state: "not-proven" };
  const parsed = answer.safeParse(await response.json().catch(() => null));
  if (!parsed.success)
    throw new Error(`Glyph linking answered ${response.status}.`);
  const body = parsed.data;
  if ("code" in body && body.code === "NOT_PROVEN")
    return { state: "not-proven" };
  if (body.wallet !== connected) return { state: "not-proven" };
  if ("code" in body)
    return { state: "elsewhere", linked: body.linked, wallet: body.wallet };
  return { state: "linked", ...body };
}
